import { useState, useEffect, useCallback } from 'react';
import {
  FileText, Plus, Printer, Search, Filter, Trash2, ChevronDown, X, Check,
  Receipt, FileCheck2, FileBadge2, FileX, FileStack, ToggleLeft, ToggleRight,
  ArrowLeft, Percent, FileDown, Settings, PlusCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DOC_TYPES, DOC_STATUSES, COMPANY,
  createDocument, updateDocument, deleteDocument,
  subscribeDocuments, calcTotals,
  getCompanySettings, saveCompanySettings,
} from '@/lib/documentsApi';
import { printDocument, INCOME_SECTIONS } from '@/lib/documentTemplates';
import { partiesApi } from '@/lib/unifiedApi';
import { canBill } from '@/lib/financeUtils';
import { logActivity } from '@/lib/activityLog';
import { useAuth } from '@/context/AuthContext';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt  = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const today = () => new Date().toISOString().slice(0, 10);

const TYPE_COLORS = {
  QT: 'bg-blue-50 text-blue-600 border-blue-200',
  IV: 'bg-indigo-50 text-indigo-600 border-indigo-200',
  RC: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  TI: 'bg-amber-50 text-amber-600 border-amber-200',
  WT: 'bg-rose-50 text-rose-600 border-rose-200',
  WC: 'bg-slate-100 text-slate-600 border-slate-300',
};

const VAT_TYPE_OPTIONS = [
  { value: 'vat7',   label: 'VAT 7%',  cls: 'text-emerald-500' },
  { value: 'zero',   label: 'VAT 0%',  cls: 'text-amber-500' },
  { value: 'exempt', label: 'ยกเว้น',  cls: 'text-slate-400'  },
];
const WHT_RATES = [0, 1, 2, 3, 5, 10];

// ─── Empty helpers ────────────────────────────────────────────────────────────
const emptyItem = () => ({
  description:    '',
  detail:         '',   // รายละเอียดย่อยใต้ชื่อรายการ
  qty:            1,
  unit:           'ครั้ง',
  unit_price:     0,
  discount_type:  'percent',
  discount_value: 0,
  vat_type:       'vat7',
});

const emptyForm = (type = 'QT') => ({
  doc_type:       type,
  price_mode:     'exclusive',
  vat_mode:       'vat',   // 'vat' = แสดง VAT ปกติ | 'none' = ไม่แสดง VAT
  issue_date:     today(),
  due_date:       '',
  credit_days:     30,
  seller_name:     '',
  job_name:        '',
  // WC — มาตรา 50 ทวิ specific
  income_section:  '40_8',
  income_description: '',
  submit_method:   1,
  issue_count:     1,
  party_id:       null,
  customer:       { name: '', tax_id: '', address: '', phone: '', email: '', branch: '' },
  items:          [emptyItem()],
  wht_rate:       0,
  notes:          '',
  ref_doc_number: '',
  status:         'draft',
  bank_account_indices: null, // null = ใช้ทุกบัญชี | [0,1,...] = เลือกเฉพาะ
});

// Shared light-theme field styles (injected once)
const FIELD_CSS = `
  .lbl{display:block;font-size:12px;color:#64748b;margin-bottom:4px;font-weight:500}
  .fld{width:100%;height:38px;border-radius:8px;background:#fff;border:1px solid #e2e8f0;color:#1e293b;padding:0 12px;font-size:14px;outline:none;transition:border-color .15s,box-shadow .15s;font-family:inherit}
  .fld:focus{border-color:#2563eb;box-shadow:0 0 0 3px rgba(37,99,235,.12)}
  .sec{font-size:11px;font-weight:700;color:#94a3b8;text-transform:uppercase;letter-spacing:.6px;margin-bottom:12px}
`;

// ─── Badges ───────────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const s = DOC_STATUSES[status] || DOC_STATUSES.draft;
  return (
    <span className="px-2 py-0.5 rounded-full text-xs font-semibold border"
      style={{ color: s.color, background: s.bg, borderColor: s.color + '44' }}>
      {s.label}
    </span>
  );
}

function TypeBadge({ type }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${TYPE_COLORS[type] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
      {type}
    </span>
  );
}

// ─── Line-item row ────────────────────────────────────────────────────────────
function ItemRow({ item, idx, onChange, onRemove, canRemove }) {
  const qty   = Number(item.qty) || 0;
  const price = Number(item.unit_price) || 0;
  const gross = qty * price;
  const discV = Number(item.discount_value) || 0;
  const disc  = item.discount_type === 'percent'
    ? Math.min(gross * discV / 100, gross)
    : Math.min(discV, gross);
  const net = gross - disc;
  const cellInput = "w-full bg-transparent text-sm text-slate-800 border-b border-slate-200 focus:border-blue-500 outline-none py-0.5";

  return (
    <tr className="border-b border-slate-100 hover:bg-slate-50 group">
      {/* # */}
      <td className="py-2 px-2 text-slate-400 text-xs text-center w-6">{idx + 1}</td>

      {/* Description + detail */}
      <td className="py-2 px-2">
        <input value={item.description} onChange={e => onChange(idx, 'description', e.target.value)}
          placeholder="ชื่อรายการ..." className={cellInput} />
        <textarea value={item.detail || ''} onChange={e => onChange(idx, 'detail', e.target.value)}
          placeholder="รายละเอียดเพิ่มเติม..."
          rows={1}
          onInput={e => { e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
          className="w-full bg-transparent text-xs text-slate-400 border-b border-slate-100 focus:border-blue-300 outline-none py-0.5 mt-1 italic placeholder:not-italic placeholder:text-slate-300 resize-none overflow-hidden leading-5" />
      </td>

      {/* Qty */}
      <td className="py-2 px-1 w-16">
        <input type="number" min="0" step="1" value={item.qty}
          onChange={e => onChange(idx, 'qty', e.target.value)}
          className={`${cellInput} text-right`} />
      </td>

      {/* Unit */}
      <td className="py-2 px-1 w-20">
        <input value={item.unit} onChange={e => onChange(idx, 'unit', e.target.value)}
          placeholder="หน่วย" className={`${cellInput} text-center text-slate-600`} />
      </td>

      {/* Unit price */}
      <td className="py-2 px-1 w-28">
        <input type="number" min="0" step="0.01" value={item.unit_price}
          onChange={e => onChange(idx, 'unit_price', e.target.value)}
          className={`${cellInput} text-right`} />
      </td>

      {/* Discount — type toggle + value */}
      <td className="py-2 px-1 w-32">
        <div className="flex items-center gap-1">
          <button onClick={() => onChange(idx, 'discount_type', item.discount_type === 'percent' ? 'amount' : 'percent')}
            className="text-xs border border-slate-300 rounded px-1 py-0.5 text-slate-500 hover:text-blue-600 hover:border-blue-400 min-w-[26px] text-center">
            {item.discount_type === 'percent' ? '%' : '฿'}
          </button>
          <input type="number" min="0" step="0.01" value={item.discount_value}
            onChange={e => onChange(idx, 'discount_value', e.target.value)}
            className={`flex-1 ${cellInput} text-right`} />
        </div>
      </td>

      {/* VAT type */}
      <td className="py-2 px-1 w-24">
        <select value={item.vat_type} onChange={e => onChange(idx, 'vat_type', e.target.value)}
          className="w-full bg-white border border-slate-200 rounded text-xs text-slate-600 px-1.5 py-1 outline-none focus:border-blue-500">
          {VAT_TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </td>

      {/* Net amount */}
      <td className="py-2 px-2 text-right text-sm text-slate-800 font-medium tabular-nums w-28">{fmt(net)}</td>

      {/* Remove */}
      <td className="py-2 px-1 w-7">
        {canRemove && (
          <button onClick={() => onRemove(idx)} className="text-slate-300 hover:text-rose-500 opacity-0 group-hover:opacity-100 transition-opacity">
            <X size={14} />
          </button>
        )}
      </td>
    </tr>
  );
}

// ─── Totals display ───────────────────────────────────────────────────────────
function TotalsPanel({ items, wht_rate, price_mode, vat_mode }) {
  const noVat    = vat_mode === 'none';
  const calcItems = noVat ? items.map(it => ({ ...it, vat_type: 'exempt' })) : items;
  const t        = calcTotals({ items: calcItems, wht_rate, price_mode });
  const hasMixed = !noVat && t.subtotal_vatable > 0 && t.subtotal_exempt > 0;
  const hasDisc  = t.total_discount > 0;
  const modeLabel = noVat ? 'ไม่รวม VAT' : price_mode === 'inclusive' ? 'รวม VAT' : 'แยก VAT';

  return (
    <div className="space-y-1.5">
      <p className="text-right text-[10px] text-slate-400 italic mb-2">* ราคา{modeLabel}</p>

      {hasDisc && <>
        <div className="flex justify-between text-sm text-slate-500">
          <span>ยอดก่อนส่วนลด</span>
          <span className="tabular-nums text-slate-800">฿{fmt(t.subtotal_before_discount)}</span>
        </div>
        <div className="flex justify-between text-sm text-slate-500">
          <span>ส่วนลดรวม</span>
          <span className="tabular-nums text-slate-400">-฿{fmt(t.total_discount)}</span>
        </div>
      </>}

      <div className="flex justify-between text-sm text-slate-500">
        <span>ยอดหลังส่วนลด{noVat ? '' : ' (excl. VAT)'}</span>
        <span className="tabular-nums text-slate-800">฿{fmt(t.subtotal)}</span>
      </div>

      {!noVat && t.vat_amount > 0 && (
        <div className="flex justify-between text-sm text-emerald-600">
          <span>VAT 7%{price_mode === 'inclusive' ? ' (รวมอยู่แล้ว)' : ''}</span>
          <span className="tabular-nums">{price_mode === 'inclusive' ? '' : '+'}฿{fmt(t.vat_amount)}</span>
        </div>
      )}

      {t.wht_amount > 0 && (
        <div className="flex justify-between text-sm text-rose-500">
          <span>หัก ณ ที่จ่าย {wht_rate}%</span>
          <span className="tabular-nums">-฿{fmt(t.wht_amount)}</span>
        </div>
      )}

      {hasMixed && (
        <div className="text-[10px] text-slate-400 border-t border-slate-200 pt-1.5 mt-1 flex gap-4">
          <span>ฐาน VAT: ฿{fmt(t.subtotal_vatable_excl)}</span>
          <span>ยกเว้น VAT: ฿{fmt(t.subtotal_exempt)}</span>
        </div>
      )}

      <div className="flex justify-between text-base font-bold text-slate-800 border-t border-slate-300 pt-2 mt-1">
        <span>รวมทั้งสิ้น</span>
        <span className="text-blue-600 tabular-nums">฿{fmt(t.total)}</span>
      </div>
    </div>
  );
}

// ─── Document form (full page) ──────────────────────────────────────────────────
function DocForm({ initial, onSave, onClose, loading, settings }) {
  const [form, setForm] = useState(initial || emptyForm());
  const [parties, setParties] = useState([]);
  const [partyMenu, setPartyMenu] = useState(false);
  const [partyQ, setPartyQ] = useState('');

  // Load the customer register for the picker (customers only, active)
  useEffect(() => {
    const unsub = partiesApi.subscribe((data) => {
      setParties(
        data
          .filter(p => p.active !== false && (p.types || []).includes('customer'))
          .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'th'))
      );
    }, () => {});
    return unsub;
  }, []);

  const set     = (f, v) => setForm(p => ({ ...p, [f]: v }));
  const setCust = (f, v) => setForm(p => ({ ...p, customer: { ...p.customer, [f]: v }, party_id: null }));

  // Fill the customer block from a party in the register (keeps fields editable)
  const selectParty = (p) => {
    setForm(prev => ({
      ...prev,
      party_id: p.id,
      customer: {
        name: p.name || '',
        tax_id: p.tax_id || '',
        address: p.address || '',
        phone: p.phone || '',
        email: p.email || '',
        branch: p.branch || '',
      },
    }));
    setPartyMenu(false);
    setPartyQ('');
  };
  const setItem = (idx, field, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: val };
    setForm(p => ({ ...p, items }));
  };
  const addItem    = () => setForm(p => ({ ...p, items: [...p.items, emptyItem()] }));
  const removeItem = (idx) => setForm(p => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  const handleSave = () => {
    if (!form.customer.name.trim()) { toast.error('กรุณากรอกชื่อลูกค้า'); return; }
    if (!form.items.some(it => it.description.trim())) { toast.error('กรุณาเพิ่มรายการอย่างน้อย 1 รายการ'); return; }
    const totals = calcTotals({ items: form.items, wht_rate: form.wht_rate, price_mode: form.price_mode });
    onSave({ ...form, ...totals });
  };

  const card = "bg-white rounded-2xl border border-slate-200 shadow-sm p-5";

  return (
    <div className="min-h-screen flex flex-col bg-slate-100">
      <style>{FIELD_CSS}</style>

      {/* Sticky header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={onClose} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800">
            <ArrowLeft size={18} />
          </button>
          <div>
            <h2 className="text-lg font-bold text-slate-800">
              {initial?.id ? 'แก้ไขเอกสาร' : 'สร้างเอกสารใหม่'}
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">{DOC_TYPES[form.doc_type]?.label} · {COMPANY.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-slate-500 hover:bg-slate-100">ยกเลิก</button>
          <button onClick={handleSave} disabled={loading}
            className="px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {loading ? 'กำลังบันทึก...' : 'บันทึกเอกสาร'}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 max-w-5xl w-full mx-auto p-5 space-y-5">

        {/* General */}
        <div className={card}>
          {/* Row 1 — type + price mode + dates */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="lbl">ประเภท</label>
              <select value={form.doc_type} onChange={e => set('doc_type', e.target.value)} className="fld">
                {Object.entries(DOC_TYPES).map(([k, v]) => (
                  <option key={k} value={k}>{k} — {v.label}</option>
                ))}
              </select>
            </div>

            {/* Price mode toggle */}
            <div>
              <label className="lbl">ราคา</label>
              <button onClick={() => set('price_mode', form.price_mode === 'exclusive' ? 'inclusive' : 'exclusive')}
                className="w-full flex items-center gap-2 h-[38px] px-3 rounded-lg border border-slate-200 bg-white text-sm hover:border-blue-500 transition-colors">
                {form.price_mode === 'exclusive'
                  ? <><ToggleLeft size={16} className="text-slate-400" /><span className="text-slate-600">แยก VAT</span></>
                  : <><ToggleRight size={16} className="text-blue-600" /><span className="text-blue-600">รวม VAT</span></>
                }
              </button>
            </div>

            {/* VAT mode toggle (No VAT) */}
            <div>
              <label className="lbl">แสดง VAT</label>
              <button onClick={() => set('vat_mode', form.vat_mode === 'none' ? 'vat' : 'none')}
                className="w-full flex items-center gap-2 h-[38px] px-3 rounded-lg border border-slate-200 bg-white text-sm hover:border-blue-500 transition-colors">
                {form.vat_mode === 'none'
                  ? <><ToggleRight size={16} className="text-amber-500" /><span className="text-amber-600">ไม่แสดง VAT</span></>
                  : <><ToggleLeft size={16} className="text-slate-400" /><span className="text-slate-600">แสดง VAT</span></>
                }
              </button>
            </div>

            <div>
              <label className="lbl">วันที่ออกเอกสาร</label>
              <input type="date" value={form.issue_date} onChange={e => set('issue_date', e.target.value)} className="fld" />
            </div>

            <div>
              <label className="lbl">กำหนดชำระ</label>
              <input type="date" value={form.due_date} onChange={e => set('due_date', e.target.value)} className="fld" />
            </div>
          </div>

          {/* Row 2 — credit / seller / job */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
            <div>
              <label className="lbl">เครดิต (วัน)</label>
              <input type="number" min="0" value={form.credit_days}
                onChange={e => set('credit_days', e.target.value)} className="fld" />
            </div>
            <div>
              <label className="lbl">ผู้ขาย / Sales</label>
              <input value={form.seller_name} onChange={e => set('seller_name', e.target.value)}
                placeholder="ชื่อผู้ขาย..." className="fld" />
            </div>
            <div>
              <label className="lbl">ชื่องาน (Job Name)</label>
              <input value={form.job_name} onChange={e => set('job_name', e.target.value)}
                placeholder="ชื่องาน / โปรเจกต์..." className="fld" />
            </div>
          </div>
        </div>

        {/* WC — มาตรา 50 ทวิ specific fields */}
        {form.doc_type === 'WC' && (
          <div className={card}>
            <h3 className="sec">ข้อมูลหนังสือรับรองฯ มาตรา 50 ทวิ</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="lbl">ประเภทเงินได้ (มาตรา 40)</label>
                <select value={form.income_section} onChange={e => set('income_section', e.target.value)} className="fld">
                  {INCOME_SECTIONS.map(s => (
                    <option key={s.key} value={s.key}>{s.label} — {s.desc}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-2">
                <label className="lbl">รายละเอียดเงินได้ (แสดงในตาราง)</label>
                <input value={form.income_description}
                  onChange={e => set('income_description', e.target.value)}
                  placeholder={INCOME_SECTIONS.find(s => s.key === form.income_section)?.desc || ''}
                  className="fld" />
              </div>
              <div>
                <label className="lbl">วิธีนำส่ง</label>
                <select value={form.submit_method} onChange={e => set('submit_method', Number(e.target.value))} className="fld">
                  <option value={1}>(1) หัก ณ ที่จ่าย</option>
                  <option value={2}>(2) ออกให้ตลอดไป</option>
                  <option value={3}>(3) ออกให้ครั้งเดียว</option>
                </select>
              </div>
              <div>
                <label className="lbl">ออกให้ครั้งที่</label>
                <input type="number" min="1" value={form.issue_count}
                  onChange={e => set('issue_count', Number(e.target.value))} className="fld" />
              </div>
            </div>
          </div>
        )}

        {/* Customer */}
        <div className={card}>
          <h3 className="sec">ข้อมูลลูกค้า</h3>

          {/* Party picker — fill from the customer register */}
          <div className="relative mb-3">
            <label className="lbl">เลือกจากทะเบียนคู่ค้า</label>
            <button type="button" onClick={() => setPartyMenu(v => !v)}
              className="fld w-full flex items-center justify-between text-left">
              <span className={form.party_id ? 'text-slate-800' : 'text-slate-400'}>
                {form.party_id
                  ? (parties.find(p => p.id === form.party_id)?.name || form.customer.name || 'เลือกลูกค้า')
                  : 'เลือกลูกค้า หรือพิมพ์เองด้านล่าง'}
              </span>
              <ChevronDown size={16} className="text-slate-400 flex-shrink-0" />
            </button>
            {partyMenu && (
              <div className="absolute z-30 mt-1 w-full rounded-lg bg-white border border-slate-200 shadow-xl max-h-64 overflow-hidden flex flex-col">
                <div className="p-2 border-b border-slate-100">
                  <input autoFocus value={partyQ} onChange={e => setPartyQ(e.target.value)}
                    placeholder="ค้นหาชื่อ / เลขภาษี" className="fld w-full text-sm" />
                </div>
                <div className="overflow-y-auto">
                  {parties
                    .filter(p => {
                      const q = partyQ.trim().toLowerCase();
                      if (!q) return true;
                      return (p.name || '').toLowerCase().includes(q) || (p.tax_id || '').includes(q);
                    })
                    .map(p => (
                      <button key={p.id} type="button" onClick={() => selectParty(p)}
                        className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm">
                        <div className="text-slate-800">{p.name}</div>
                        <div className="text-slate-400 text-xs">
                          {p.code}{p.tax_id ? ` · ${p.tax_id}` : ''}
                        </div>
                      </button>
                    ))}
                  {parties.length === 0 && (
                    <div className="px-3 py-4 text-center text-slate-400 text-sm">
                      ยังไม่มีคู่ค้าในทะเบียน — เพิ่มได้ที่เมนู "คู่ค้า"
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="lbl">ชื่อบริษัท / ชื่อลูกค้า *</label>
              <input value={form.customer.name} onChange={e => setCust('name', e.target.value)}
                placeholder="บริษัท..." className="fld" />
            </div>
            <div>
              <label className="lbl">เลขประจำตัวผู้เสียภาษี</label>
              <input value={form.customer.tax_id} onChange={e => setCust('tax_id', e.target.value)}
                placeholder="0000000000000" className="fld" />
            </div>
            <div>
              <label className="lbl">สาขา</label>
              <input value={form.customer.branch || ''} onChange={e => setCust('branch', e.target.value)}
                placeholder="สำนักงานใหญ่ / 00001" className="fld" />
            </div>
            <div>
              <label className="lbl">เบอร์โทร</label>
              <input value={form.customer.phone} onChange={e => setCust('phone', e.target.value)}
                placeholder="0xx-xxx-xxxx" className="fld" />
            </div>
            <div className="col-span-2">
              <label className="lbl">ที่อยู่</label>
              <input value={form.customer.address} onChange={e => setCust('address', e.target.value)}
                placeholder="ที่อยู่..." className="fld" />
            </div>
            <div>
              <label className="lbl">อีเมล</label>
              <input value={form.customer.email} onChange={e => setCust('email', e.target.value)}
                placeholder="email@example.com" className="fld" />
            </div>
            <div>
              <label className="lbl">อ้างอิงเอกสาร (เลขที่)</label>
              <input value={form.ref_doc_number} onChange={e => set('ref_doc_number', e.target.value)}
                placeholder="เช่น QT-2026-0001" className="fld" />
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className={card}>
          <h3 className="sec">รายการสินค้า / บริการ</h3>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px]">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="py-2 px-2 text-left text-xs text-slate-400 w-6">#</th>
                  <th className="py-2 px-2 text-left text-xs text-slate-400">รายการ</th>
                  <th className="py-2 px-1 text-right text-xs text-slate-400 w-16">จำนวน</th>
                  <th className="py-2 px-1 text-center text-xs text-slate-400 w-20">หน่วย</th>
                  <th className="py-2 px-1 text-right text-xs text-slate-400 w-28">ราคา/หน่วย</th>
                  <th className="py-2 px-1 text-right text-xs text-slate-400 w-32">ส่วนลด (฿/%)</th>
                  <th className="py-2 px-1 text-center text-xs text-slate-400 w-24">VAT</th>
                  <th className="py-2 px-2 text-right text-xs text-slate-400 w-28">จำนวนเงิน</th>
                  <th className="w-7" />
                </tr>
              </thead>
              <tbody>
                {form.items.map((item, i) => (
                  <ItemRow key={i} item={item} idx={i}
                    onChange={setItem} onRemove={removeItem}
                    canRemove={form.items.length > 1} />
                ))}
              </tbody>
            </table>
          </div>
          <button onClick={addItem}
            className="mt-3 text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1.5">
            <Plus size={13} /> เพิ่มรายการ
          </button>
        </div>

        {/* WHT + Notes + Totals */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className={`${card} space-y-3`}>
            <div>
              <label className="lbl">หัก ณ ที่จ่าย (%)</label>
              <select value={form.wht_rate} onChange={e => set('wht_rate', Number(e.target.value))} className="fld">
                {WHT_RATES.map(r => <option key={r} value={r}>{r === 0 ? 'ไม่หัก' : `${r}%`}</option>)}
              </select>
            </div>
            <div>
              <label className="lbl">หมายเหตุ</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                rows={3} placeholder="หมายเหตุ..."
                className="w-full rounded-lg bg-white border border-slate-200 text-slate-800 px-3 py-2 text-sm focus:border-blue-500 outline-none resize-none" />
            </div>
            {/* Bank account selector */}
            {(() => {
              const accounts = settings?.bank_accounts || COMPANY.bank_accounts;
              if (!accounts || accounts.length < 2) return null;
              const sel = form.bank_account_indices ?? accounts.map((_, i) => i);
              return (
                <div>
                  <label className="lbl">บัญชีรับชำระที่แสดงในเอกสาร</label>
                  <div className="space-y-1.5 mt-1">
                    {accounts.map((a, i) => {
                      const checked = sel.includes(i);
                      return (
                        <label key={i} className="flex items-start gap-2.5 cursor-pointer group">
                          <input type="checkbox" checked={checked} onChange={() => {
                            const next = checked ? sel.filter(x => x !== i) : [...sel, i].sort();
                            set('bank_account_indices', next.length === accounts.length ? null : next);
                          }} className="mt-0.5 accent-blue-600" />
                          <span className="text-sm text-slate-700 leading-snug">
                            <span className="font-medium">{a.bank}</span>
                            {a.branch ? ` สาขา${a.branch}` : ''} · {a.account_type} <span className="text-blue-600 font-mono">{a.account_number}</span>
                            <br /><span className="text-slate-400 text-xs">{a.account_name}</span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
          <div className={card}>
            <TotalsPanel items={form.items} wht_rate={form.wht_rate} price_mode={form.price_mode} vat_mode={form.vat_mode} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function BillingPage() {
  const { user } = useAuth();
  const [docs, setDocs]             = useState([]);
  const [showForm, setShowForm]     = useState(false);
  const [editDoc, setEditDoc]       = useState(null);
  const [saving, setSaving]         = useState(false);
  const [searchQ, setSearchQ]       = useState('');
  const [filterType, setFilterType]     = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showTypeMenu, setShowTypeMenu]     = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [depositModal, setDepositModal]     = useState(null); // QT doc | null
  const [depositPct, setDepositPct]         = useState(50);
  const [settings, setSettings]             = useState({ bank_accounts: COMPANY.bank_accounts });
  const [showSettings, setShowSettings]     = useState(false);
  const [editSettings, setEditSettings]     = useState(null); // draft while modal open
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    const unsub = subscribeDocuments(setDocs);
    return unsub;
  }, []);

  useEffect(() => {
    getCompanySettings().then(s => setSettings(s)).catch(() => {});
  }, []);

  const filtered = docs.filter(d => {
    const q = searchQ.toLowerCase();
    const matchQ = !q || d.doc_number?.toLowerCase().includes(q) || d.customer?.name?.toLowerCase().includes(q);
    return matchQ && (!filterType || d.doc_type === filterType) && (!filterStatus || d.status === filterStatus);
  });

  const handleSave = useCallback(async (data) => {
    setSaving(true);
    try {
      if (editDoc?.id) {
        await updateDocument(editDoc.id, data);
        logActivity(user?.id, user?.name, 'doc_update', `แก้ไข ${editDoc.doc_number || data.doc_type} · ${data.customer?.name || ''}`);
        toast.success('อัพเดทเอกสารแล้ว');
      } else {
        const created = await createDocument(data);
        logActivity(user?.id, user?.name, 'doc_create', `${created?.doc_number || data.doc_type} · ${data.customer?.name || ''} · ฿${fmt(data.total)}`);
        toast.success('สร้างเอกสารสำเร็จ');
      }
      setShowForm(false);
      setEditDoc(null);
    } catch (e) {
      toast.error('เกิดข้อผิดพลาด: ' + e.message);
    } finally {
      setSaving(false);
    }
  }, [editDoc, user]);

  const handleDelete = async (doc) => {
    if (!window.confirm(`ลบ ${doc.doc_number} ?`)) return;
    try {
      await deleteDocument(doc.id);
      logActivity(user?.id, user?.name, 'doc_delete', `${doc.doc_number} · ${doc.customer?.name || ''}`);
      toast.success('ลบเอกสารแล้ว');
    }
    catch (e) { toast.error('ลบไม่ได้: ' + e.message); }
  };

  const handleChangeStatus = async (doc, status) => {
    try {
      await updateDocument(doc.id, { status });
      logActivity(user?.id, user?.name, 'doc_status', `${doc.doc_number} → ${DOC_STATUSES[status]?.label || status}`);
      toast.success('อัพเดทสถานะแล้ว');
    }
    catch (e) { toast.error(e.message); }
  };

  const openForm = (doc = null) => { setEditDoc(doc); setShowForm(true); };

  const handleCreateDeposit = useCallback(async () => {
    if (!depositModal) return;
    const src = depositModal;
    const pct = depositPct;
    // คำนวณยอดรวมก่อน WHT ของ QT ต้นฉบับ
    const srcItems = (src.vat_mode === 'none')
      ? (src.items || []).map(it => ({ ...it, vat_type: 'exempt' }))
      : (src.items || []);
    const t = calcTotals({ items: srcItems, wht_rate: src.wht_rate || 0, price_mode: src.price_mode || 'exclusive' });
    const grandTotal = t.total + t.wht_amount; // ยอดก่อนหัก WHT
    const depositAmt = Math.round(grandTotal * pct / 100 * 100) / 100;
    const firstVatType = (src.items || [])[0]?.vat_type || 'vat7';

    const ivData = {
      doc_type:       'IV',
      price_mode:     src.price_mode || 'exclusive',
      vat_mode:       src.vat_mode || 'vat',
      issue_date:     today(),
      due_date:       '',
      credit_days:    src.credit_days ?? 30,
      seller_name:    src.seller_name || '',
      job_name:       src.job_name || '',
      income_section: '40_8',
      income_description: '',
      submit_method:  1,
      issue_count:    1,
      party_id:       src.party_id || null,
      customer:       { ...src.customer },
      wht_rate:       0,
      notes:          '',
      ref_doc_number: src.doc_number || '',
      status:         'draft',
      items: [{
        description:    `มัดจำ ${pct}% ตามใบเสนอราคาเลขที่ ${src.doc_number}`,
        detail:         '',
        qty:            1,
        unit:           'ครั้ง',
        unit_price:     depositAmt,
        discount_type:  'percent',
        discount_value: 0,
        vat_type:       firstVatType,
      }],
    };
    setSaving(true);
    try {
      const created = await createDocument(ivData);
      logActivity(user?.id, user?.name, 'doc_create', `${created?.doc_number} (มัดจำ ${pct}% จาก ${src.doc_number}) · ฿${fmt(depositAmt)}`);
      toast.success(`สร้างใบวางบิลมัดจำ ${pct}% สำเร็จ — ${created?.doc_number}`);
      setDepositModal(null);
    } catch (e) {
      toast.error('เกิดข้อผิดพลาด: ' + e.message);
    } finally {
      setSaving(false);
    }
  }, [depositModal, depositPct, user]);

  const sumPaid = docs.filter(d => d.status === 'paid').reduce((s, d) => s + (d.total || 0), 0);

  // Admin or billing-staff (the nav hides it, but block direct /billing access too)
  if (!canBill(user)) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center p-6 bg-slate-100">
        <FileX size={40} className="text-slate-300 mb-3" />
        <p className="text-slate-400">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
      </div>
    );
  }

  // Full-page create/edit form
  if (showForm) {
    return (
      <DocForm
        initial={editDoc?.id ? { ...editDoc } : emptyForm(editDoc?.doc_type)}
        onSave={handleSave}
        onClose={() => { setShowForm(false); setEditDoc(null); }}
        loading={saving}
        settings={settings}
      />
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 text-slate-800 p-5 md:p-6 space-y-5">
      <style>{FIELD_CSS}</style>

      {/* Page header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Billing</h1>
          <p className="text-slate-400 text-sm mt-1">เอกสารทางการเงิน — {COMPANY.name}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setEditSettings(JSON.parse(JSON.stringify(settings))); setShowSettings(true); }}
            title="ตั้งค่าบัญชีธนาคาร"
            className="p-2.5 rounded-xl border border-slate-200 bg-white text-slate-500 hover:text-slate-800 hover:border-slate-300 transition-colors">
            <Settings size={16} />
          </button>
          <button onClick={() => openForm()}
            className="flex items-center gap-2 bg-blue-600 text-white font-semibold px-4 py-2.5 rounded-xl text-sm hover:bg-blue-700 transition-colors">
            <Plus size={16} /> สร้างเอกสาร
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'ร่าง',     value: docs.filter(d => d.status === 'draft').length, color: '#64748b' },
          { label: 'ส่งแล้ว', value: docs.filter(d => d.status === 'sent').length,  color: '#2563eb' },
          { label: 'ชำระแล้ว',value: docs.filter(d => d.status === 'paid').length,  color: '#16a34a' },
          { label: 'ยอดรับรวม',value: `฿${fmt(sumPaid)}`,                            color: '#2563eb' },
        ].map(k => (
          <div key={k.label} className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4">
            <p className="text-xs text-slate-400">{k.label}</p>
            <p className="text-2xl font-bold mt-1" style={{ color: k.color }}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* Quick-create chips */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(DOC_TYPES).map(([k, v]) => (
          <button key={k} onClick={() => openForm({ doc_type: k })}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-opacity ${TYPE_COLORS[k]} hover:opacity-70`}>
            <FileText size={11} /> {v.label}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl px-3 py-2 flex-1 min-w-48 shadow-sm">
          <Search size={14} className="text-slate-400" />
          <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
            placeholder="ค้นหาเลขที่หรือชื่อลูกค้า..."
            className="bg-transparent text-sm text-slate-800 outline-none flex-1 placeholder-slate-400" />
        </div>

        <div className="relative">
          <button onClick={() => { setShowTypeMenu(!showTypeMenu); setShowStatusMenu(false); }}
            className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-600 hover:border-slate-300 shadow-sm">
            <Filter size={14} /> {filterType ? DOC_TYPES[filterType]?.label : 'ประเภท'} <ChevronDown size={12} />
          </button>
          {showTypeMenu && (
            <div className="absolute top-full mt-1 left-0 bg-white border border-slate-200 rounded-xl shadow-xl z-20 min-w-44 overflow-hidden">
              <button onClick={() => { setFilterType(''); setShowTypeMenu(false); }}
                className="w-full text-left px-4 py-2.5 text-sm text-slate-400 hover:bg-slate-50">ทั้งหมด</button>
              {Object.entries(DOC_TYPES).map(([k, v]) => (
                <button key={k} onClick={() => { setFilterType(k); setShowTypeMenu(false); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50">
                  {k} — {v.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="relative">
          <button onClick={() => { setShowStatusMenu(!showStatusMenu); setShowTypeMenu(false); }}
            className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-xl px-3 py-2 text-sm text-slate-600 hover:border-slate-300 shadow-sm">
            {filterStatus ? DOC_STATUSES[filterStatus]?.label : 'สถานะ'} <ChevronDown size={12} />
          </button>
          {showStatusMenu && (
            <div className="absolute top-full mt-1 left-0 bg-white border border-slate-200 rounded-xl shadow-xl z-20 min-w-36 overflow-hidden">
              <button onClick={() => { setFilterStatus(''); setShowStatusMenu(false); }}
                className="w-full text-left px-4 py-2.5 text-sm text-slate-400 hover:bg-slate-50">ทั้งหมด</button>
              {Object.entries(DOC_STATUSES).map(([k, v]) => (
                <button key={k} onClick={() => { setFilterStatus(k); setShowStatusMenu(false); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50">{v.label}</button>
              ))}
            </div>
          )}
        </div>

        {(filterType || filterStatus) && (
          <button onClick={() => { setFilterType(''); setFilterStatus(''); }}
            className="text-xs text-slate-400 hover:text-slate-700 flex items-center gap-1">
            <X size={12} /> ล้าง
          </button>
        )}
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {['เลขที่เอกสาร','ประเภท','ลูกค้า','วันที่','ยอดรวม','สถานะ',''].map((h, i) => (
                <th key={i} className={`py-3 px-4 text-xs text-slate-500 font-medium ${i >= 4 ? 'text-center' : 'text-left'}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="py-12 text-center text-slate-400 text-sm">
                {docs.length === 0 ? 'ยังไม่มีเอกสาร — กด "สร้างเอกสาร" เพื่อเริ่มต้น' : 'ไม่พบเอกสารที่ค้นหา'}
              </td></tr>
            )}
            {filtered.map(doc => (
              <tr key={doc.id} className="border-b border-slate-100 hover:bg-slate-50 group">
                <td className="py-3 px-4">
                  <span className="text-sm font-mono text-blue-600 font-semibold">{doc.doc_number}</span>
                </td>
                <td className="py-3 px-4"><TypeBadge type={doc.doc_type} /></td>
                <td className="py-3 px-4 text-sm text-slate-600 max-w-[160px] truncate">{doc.customer?.name || '-'}</td>
                <td className="py-3 px-4 text-sm text-slate-400">{doc.issue_date || '-'}</td>
                <td className="py-3 px-4 text-right text-sm text-slate-800 font-medium tabular-nums">฿{fmt(doc.total)}</td>
                <td className="py-3 px-4 text-center">
                  <div className="flex items-center justify-center gap-1 group/s">
                    <StatusBadge status={doc.status} />
                    <div className="relative">
                      <button className="text-slate-300 hover:text-slate-600 opacity-0 group-hover:opacity-100"><ChevronDown size={11} /></button>
                      <div className="absolute hidden group-hover/s:block top-5 right-0 bg-white border border-slate-200 rounded-xl shadow-xl z-10 min-w-28 overflow-hidden">
                        {Object.entries(DOC_STATUSES).map(([k, v]) => (
                          <button key={k} onClick={() => handleChangeStatus(doc, k)}
                            className="w-full text-left px-3 py-2 text-xs text-slate-600 hover:bg-slate-50">{v.label}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {doc.doc_type === 'QT' && (
                      <button onClick={() => { setDepositPct(50); setDepositModal(doc); }}
                        title="สร้างมัดจำ" className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50">
                        <Percent size={14} />
                      </button>
                    )}
                    {(() => {
                      const allAccounts = settings?.bank_accounts || COMPANY.bank_accounts;
                      const idx = doc.bank_account_indices;
                      const filteredAccounts = idx ? allAccounts.filter((_, i) => idx.includes(i)) : allAccounts;
                      const docSettings = { ...settings, bank_accounts: filteredAccounts };
                      return (<>
                        <button onClick={() => printDocument(doc, { error: toast.error }, 'preview', docSettings)}
                          title="พิมพ์ / ดูตัวอย่าง" className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50">
                          <Printer size={14} />
                        </button>
                        <button onClick={() => printDocument(doc, { error: toast.error }, 'pdf', docSettings)}
                          title="บันทึก PDF" className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50">
                          <FileDown size={14} />
                        </button>
                      </>);
                    })()}
                    <button onClick={() => openForm(doc)}
                      title="แก้ไข" className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50">
                      <FileCheck2 size={14} />
                    </button>
                    <button onClick={() => handleDelete(doc)}
                      title="ลบ" className="p-1.5 rounded-lg text-slate-400 hover:text-rose-500 hover:bg-rose-50">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length > 0 && (
          <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100">
            แสดง {filtered.length} จาก {docs.length} รายการ
          </div>
        )}
      </div>

      {(showTypeMenu || showStatusMenu) && (
        <div className="fixed inset-0 z-10" onClick={() => { setShowTypeMenu(false); setShowStatusMenu(false); }} />
      )}

      {/* ─── Deposit Modal ──────────────────────────────────────────────── */}
      {depositModal && (() => {
        const src = depositModal;
        const srcItems = (src.vat_mode === 'none')
          ? (src.items || []).map(it => ({ ...it, vat_type: 'exempt' }))
          : (src.items || []);
        const t = calcTotals({ items: srcItems, wht_rate: src.wht_rate || 0, price_mode: src.price_mode || 'exclusive' });
        const grandTotal = t.total + t.wht_amount;
        const depositAmt = Math.round(grandTotal * depositPct / 100 * 100) / 100;
        const DEPOSIT_PCTS = [10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 100];
        return (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
              <div className="bg-indigo-600 px-5 py-4 flex items-center justify-between">
                <div>
                  <div className="text-white font-bold text-base">สร้างใบวางบิลมัดจำ</div>
                  <div className="text-indigo-200 text-xs mt-0.5">{src.doc_number} · {src.customer?.name || '-'}</div>
                </div>
                <button onClick={() => setDepositModal(null)} className="text-indigo-200 hover:text-white">
                  <X size={18} />
                </button>
              </div>

              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 mb-2">เลือกเปอร์เซ็นต์มัดจำ</label>
                  <div className="grid grid-cols-4 gap-2">
                    {DEPOSIT_PCTS.map(p => (
                      <button key={p} onClick={() => setDepositPct(p)}
                        className={`py-2 rounded-lg text-sm font-semibold border transition-colors
                          ${depositPct === p
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-400'}`}>
                        {p}%
                      </button>
                    ))}
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <span className="text-xs text-slate-400">หรือกำหนดเอง</span>
                    <input type="number" min="1" max="100" value={depositPct}
                      onChange={e => setDepositPct(Math.min(100, Math.max(1, Number(e.target.value))))}
                      className="w-20 text-center border border-slate-200 rounded-lg text-sm py-1.5 focus:border-indigo-400 outline-none" />
                    <span className="text-xs text-slate-400">%</span>
                  </div>
                </div>

                <div className="bg-indigo-50 rounded-xl px-4 py-3">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>ยอดรวมทั้งสิ้น (QT)</span>
                    <span>฿{fmt(grandTotal)}</span>
                  </div>
                  <div className="flex justify-between text-base font-bold text-indigo-700">
                    <span>ยอดมัดจำ {depositPct}%</span>
                    <span>฿{fmt(depositAmt)}</span>
                  </div>
                </div>

                <div className="flex gap-2 pt-1">
                  <button onClick={() => setDepositModal(null)}
                    className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-500 text-sm hover:bg-slate-50 font-medium">
                    ยกเลิก
                  </button>
                  <button onClick={handleCreateDeposit} disabled={saving}
                    className="flex-1 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center gap-2">
                    <Receipt size={15} />
                    สร้างใบวางบิล
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Settings modal: bank accounts ── */}
      {showSettings && editSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2 font-bold text-slate-800">
                <Settings size={16} className="text-slate-500" />
                ตั้งค่าบัญชีธนาคาร
              </div>
              <button onClick={() => setShowSettings(false)} className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"><X size={16} /></button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              {(editSettings.bank_accounts || []).map((acc, i) => (
                <div key={i} className="border border-slate-200 rounded-xl p-4 space-y-3 relative">
                  <button onClick={() => setEditSettings(s => ({ ...s, bank_accounts: s.bank_accounts.filter((_, j) => j !== i) }))}
                    className="absolute top-3 right-3 p-1 rounded-lg hover:bg-rose-50 text-slate-300 hover:text-rose-500"><X size={13} /></button>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ['ธนาคาร', 'bank'], ['สาขา', 'branch'],
                      ['ประเภทบัญชี', 'account_type'], ['เลขบัญชี', 'account_number'],
                      ['ชื่อบัญชี', 'account_name'],
                    ].map(([label, field]) => (
                      <div key={field} className={field === 'account_name' ? 'col-span-2' : ''}>
                        <label className="block text-xs text-slate-400 mb-1">{label}</label>
                        <input value={acc[field] || ''} onChange={e => {
                          const v = e.target.value;
                          setEditSettings(s => {
                            const arr = [...s.bank_accounts];
                            arr[i] = { ...arr[i], [field]: v };
                            return { ...s, bank_accounts: arr };
                          });
                        }}
                          className="w-full border border-slate-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400" />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              <button onClick={() => setEditSettings(s => ({
                ...s,
                bank_accounts: [...(s.bank_accounts || []), { bank: '', branch: '', account_type: 'ออมทรัพย์', account_number: '', account_name: COMPANY.name }]
              }))}
                className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium py-1">
                <PlusCircle size={14} /> เพิ่มบัญชีธนาคาร
              </button>
            </div>
            <div className="flex gap-2 px-5 py-4 border-t border-slate-100">
              <button onClick={() => setShowSettings(false)}
                className="flex-1 py-2 rounded-xl border border-slate-200 text-slate-500 text-sm hover:bg-slate-50">ยกเลิก</button>
              <button disabled={savingSettings} onClick={async () => {
                setSavingSettings(true);
                try {
                  await saveCompanySettings({ bank_accounts: editSettings.bank_accounts });
                  setSettings(editSettings);
                  setShowSettings(false);
                  toast.success('บันทึกข้อมูลบัญชีธนาคารแล้ว');
                } catch (e) {
                  toast.error('บันทึกไม่สำเร็จ: ' + e.message);
                } finally { setSavingSettings(false); }
              }}
                className="flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                <Check size={14} /> บันทึก
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
